import * as XLSX from "xlsx";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useState } from "react";
import moment from "moment-timezone";
import { Label } from "../../components/ui/label";
import { Loader } from "../../components/ui/loader";
import { Input } from "../../components/ui/input";
import {
  TicketRunSummary,
  TicketsSummary,
  Provider,
} from "../../lib/models/index";
import {
  TicketProviderSummary,
  TicketProviderRecentRunPartial,
  TicketProviderBatchDownloadPartial,
} from "../../lib/models/tickets";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { processFromFiles, processOldCogsReport } from "../../lib/cogsReport";
import { useToast } from "../../components/ui/use-toast";
import { useListCustomers } from "../../lib/api/customer";
import { Skeleton } from "../../components/ui/skeleton";
import { Separator } from "../../components/ui/separator";
import { downloadBinaryData } from "../../lib/browser/download";
import { processTicketDetailsFiles, ErrorsData as TicketDetailsErrorsData } from "@/lib/cogsReport/ticketBreakdown";

export function CogsReports() {
  return <ReportUploadView />;
}

export function ReportUploadView({ }: {}) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [originalReportFile, setOriginalReportFile] = useState<File | undefined>(undefined);
  const [reportFiles, setNewReportFile] = useState<File[] | undefined>(undefined);
  const [unsettledFiles, setNewUnsettledFile] = useState<File[] | undefined>(undefined);
  const [ticketDetailCogsFiles, setTicketDetailCogsFiles] = useState<File[] | undefined>(undefined);
  const [ticketDetailSalesFiles, setTicketDetailSalesFiles] = useState<File[] | undefined>(undefined);
  const [ticketDetailErrors, setTicketDetailErrors] = useState<TicketDetailsErrorsData | undefined>(undefined);
  const customersListRes = useListCustomers();

  if ([customersListRes].some((result) => result.isLoading)) {
    return (
      <div className="flex flex-col space-y-2">
        <Skeleton className="h-4 w-[250px]" />
        <Skeleton className="h-4 w-[200px]" />
      </div>
    );
  }

  if (
    [customersListRes].some(
      (result) => result.error || !result?.data?.success,
    )
  ) {
    return (
      <div className="flex flex-col space-y-2">
        Failed to load Customer data. Please refresh and try again
      </div>
    );
  }

  const onUploadFile = async () => {
    if (!originalReportFile && !reportFiles && !unsettledFiles) {
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const date = moment().format("YYYY_MM_DDTHH:mm");
      const { cogsOutputWorkbook, unsettledOutputWorkbook, error } = originalReportFile ? await processOldCogsReport({ reportFile: originalReportFile }) :
        await processFromFiles({
          customers: customersListRes?.data?.data || [],
          reportFiles,
          unsettledFiles
        });

      if (cogsOutputWorkbook) {
        const wbBuffer = await cogsOutputWorkbook.xlsx.writeBuffer();
        downloadBinaryData(
          wbBuffer,
          originalReportFile ? `sales_${date}.xlsx` : `cogs_${date}.xlsx`,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        );
      }


      if (unsettledOutputWorkbook) {
        const wbBuffer = await unsettledOutputWorkbook.xlsx.writeBuffer();
        downloadBinaryData(
          wbBuffer,
          `unsettled_${date}.xlsx`,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        );
      }

      if (error) {
        throw error;
      }

      toast({
        title: "Generated",
        description: "",
      });
    } catch (error) {
      console.error("COGS report error", error)
      toast({
        title: "Failed to process",
        description: "",
      });
    }

    setIsSubmitting(false);
  };

  const onUploadTicketDetailFile = async () => {
    if (!ticketDetailCogsFiles && !ticketDetailSalesFiles) {
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const date = moment().format("YYYY_MM_DDTHH:mm");
      const { cogsOutputWorkbook: cogsBuffer, unsettledOutputWorkbook: unsettledBuffer, error, errors } = await processTicketDetailsFiles({
        customers: customersListRes?.data?.data || [],
        reportFiles: ticketDetailCogsFiles,
        unsettledFiles: ticketDetailSalesFiles
      });
      setTicketDetailErrors(errors);

      if (cogsBuffer) {
        downloadBinaryData(
          cogsBuffer,
          `ticket_details_cogs_${date}.xlsx`,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        );
      }


      if (unsettledBuffer) {
        downloadBinaryData(
          unsettledBuffer,
          `ticket_details_sales_${date}.xlsx`,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        );
      }

      if (error) {
        throw error;
      }

      toast({
        title: "Generated",
        description: "",
      });
    } catch (error) {
      console.error("Ticket Details COGS report error", error)
      toast({
        title: "Failed to process",
        description: "",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSingleFileChange = (setMethod: any) => (...params: any[]) => {
    const item = params[0];
    setMethod(item.target.files[0]);
  };

  const onMultiFileChange = (setMethod: any) => (...params: any[]) => {
    const item = params[0];
    setMethod(Array.from(item.target.files));
  };

  return (
    <>
      <div className="container flex flex-col mb-2">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          Upload Sales and Purchase Reports
        </h1>
      </div>
      <div className="flex items-start flex-col h-full mt-4">
        <div className="container flex flex-col gap-4">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="cogsFile">COGS Report</Label>
            <Input
              id="cogsFile"
              type="file"
              accept=".xlsx"
              onChange={onSingleFileChange(setOriginalReportFile)}
            />
          </div>

          <Separator className="my-4" />
          <h2 className="scroll-m-20 text-xl font-extrabold tracking-tight lg:text-2xl">
            State & Providence
          </h2>
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="newCogsFile">COGS Reports</Label>
            <Input
              id="newCogsFile"
              multiple
              type="file"
              accept=".xlsx"
              onChange={onMultiFileChange(setNewReportFile)}
            />
          </div>

          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="unsettled">Unsettled Files</Label>
            <Input
              id="unsettled"
              multiple
              type="file"
              accept=".xlsx"
              onChange={onMultiFileChange(setNewUnsettledFile)}
            />
          </div>

          <div className="grid w-full max-w-sm items-center gap-1.5 mt-2">
            <Button
              disabled={isSubmitting || (!originalReportFile && !reportFiles && !unsettledFiles)}
              id="uploadButton"
              onClick={onUploadFile}
            >
              {isSubmitting ? <Loader /> : "Process"}
            </Button>
          </div>
        </div>
        <div className="container flex flex-col gap-4">
          <Separator className="my-4" />
          <h2 className="scroll-m-20 text-xl font-extrabold tracking-tight lg:text-2xl">
            Ticket Details
          </h2>
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="newTicketDetailsCogsFile">COGS Reports</Label>
            <Input
              id="newTicketDetailsCogsFile"
              multiple
              type="file"
              accept=".xlsx"
              onChange={onMultiFileChange(setTicketDetailCogsFiles)}
            />
          </div>

          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="newTicketDetailsSalesFiles">Sales Files</Label>
            <Input
              id="newTicketDetailsSalesFiles"
              multiple
              type="file"
              accept=".xlsx"
              onChange={onMultiFileChange(setTicketDetailSalesFiles)}
            />
          </div>

          <div className="grid w-full max-w-sm items-center gap-1.5 mt-2">
            <Button
              disabled={isSubmitting || (!ticketDetailCogsFiles && !ticketDetailSalesFiles)}
              id="uploadButton"
              onClick={onUploadTicketDetailFile}
            >
              {isSubmitting ? <Loader /> : "Process"}
            </Button>
          </div>
          {ticketDetailErrors && (<RenderTicketDetailErrors ticketDetailErrors={ticketDetailErrors} />)}
        </div>
      </div>
    </>
  );
}

function RenderTicketDetailErrors({ ticketDetailErrors }: { ticketDetailErrors: TicketDetailsErrorsData }) {
  const memoizedData = React.useMemo(() => {
    const { missing_customers, failed_to_find_customers, failed_to_find_commodity } = ticketDetailErrors;

    const missingVendorTickets = missing_customers
      .filter(row => row["Ticket Number"])
      .slice(0, 4)
      .map(row => row["Ticket Number"]);

    const unmatchedVendors = Object.entries(failed_to_find_customers).map(([vendorName, rows]) => ({
      vendorName,
      count: rows.length,
      ticketExamples: rows
        .filter(row => row["Ticket Number"])
        .slice(0, 4)
        .map(row => row["Ticket Number"])
    }));

    const unidentifiedCommodities = Object.entries(failed_to_find_commodity).map(([commodityId, rows]) => ({
      commodityId,
      count: rows.length,
      ticketExamples: rows
        .filter(row => row["Ticket Number"])
        .slice(0, 4)
        .map(row => row["Ticket Number"])
    }));

    return {
      missingVendorCount: missing_customers.length,
      missingVendorTickets,
      unmatchedVendors,
      unidentifiedCommodities
    };
  }, [ticketDetailErrors]);

  const { missingVendorCount, missingVendorTickets, unmatchedVendors, unidentifiedCommodities } = memoizedData;

  if (missingVendorCount === 0 && unmatchedVendors.length === 0 && unidentifiedCommodities.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 space-y-6">
      <h3 className="text-lg font-semibold text-red-600">Ticket Details - Processing Errors</h3>

      <Accordion type="multiple" className="w-full border rounded-md">
        {missingVendorCount > 0 && (
          <AccordionItem value="missing-vendors">
            <AccordionTrigger className="px-4 hover:bg-slate-50">
              <div className="flex items-center space-x-2">
                <span className="font-medium">Missing Vendor Names</span>
                <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-medium">
                  {missingVendorCount} rows
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-2">
                <p>Found {missingVendorCount} rows without vendor names. These records cannot be processed.</p>

                {missingVendorTickets.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium mb-1">Example Ticket Numbers:</p>
                    <div className="flex flex-wrap gap-2">
                      {missingVendorTickets.map((ticket, index) => (
                        <span key={`missingVendorTickets_${index}`} className="bg-slate-100 px-2 py-1 rounded text-sm">
                          {ticket}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {unmatchedVendors.length > 0 && (
          <AccordionItem value="unmatched-vendors">
            <AccordionTrigger className="px-4 hover:bg-slate-50">
              <div className="flex items-center space-x-2">
                <span className="font-medium">Unmatched Vendor Names</span>
                <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full text-xs font-medium">
                  {unmatchedVendors.length} vendors
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-4">
                <p>Found {unmatchedVendors.length} vendor names that couldn't be matched to clients/vendors in our database.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {unmatchedVendors.map((vendor, index) => (
                    <div key={`unmatchedVendors_${index}`} className="border rounded-md p-3 bg-slate-50">
                      <p className="font-medium truncate" title={vendor.vendorName}>{vendor.vendorName}</p>
                      <p className="text-sm text-slate-600">{vendor.count} records</p>

                      {vendor.ticketExamples.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-slate-500 mb-1">Example Tickets:</p>
                          <div className="flex flex-wrap gap-1">
                            {vendor.ticketExamples.map((ticket, tIndex) => (
                              <span key={`unmatchedVendorsTicketExamples_${tIndex}`} className="bg-slate-200 px-1.5 py-0.5 rounded text-xs">
                                {ticket}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {unidentifiedCommodities.length > 0 && (
          <AccordionItem value="unidentified-commodities">
            <AccordionTrigger className="px-4 hover:bg-slate-50">
              <div className="flex items-center space-x-2">
                <span className="font-medium">Unidentified Commodities</span>
                <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-medium">
                  {unidentifiedCommodities.length} commodities
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-4">
                <p>Found {unidentifiedCommodities.length} commodity IDs that couldn't be identified in our system.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {unidentifiedCommodities.map((commodity, index) => (
                    <div key={`unidentifiedCommodities_${index}`} className="border rounded-md p-3 bg-slate-50">
                      <p className="font-medium truncate" title={commodity.commodityId}>{commodity.commodityId}</p>
                      <p className="text-sm text-slate-600">{commodity.count} records</p>

                      {commodity.ticketExamples.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-slate-500 mb-1">Example Tickets:</p>
                          <div className="flex flex-wrap gap-1">
                            {commodity.ticketExamples.map((ticket, tIndex) => (
                              <span key={`unidentifiedCommoditiesTicketExamples_${tIndex}`} className="bg-slate-200 px-1.5 py-0.5 rounded text-xs">
                                {ticket}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
}
